import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconLoader2, IconReceipt, IconBell, IconUsersGroup,
  IconMail, IconCheck, IconExternalLink, IconRefresh, IconTrash,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

export default function Actividad() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("purchases"); // "purchases" | "notifications"

  const [purchases, setPurchases] = useState([]);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, n] = await Promise.all([
        api.get("/admin/purchases?limit=50"),
        api.get("/admin/panel-notifications?limit=40"),
      ]);
      setPurchases(p.data.items || []);
      setTotalPurchases(p.data.total || 0);
      setNotifs(n.data.items || []);
      setUnreadCount(n.data.unread_count || 0);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/admin/panel-notifications/mark-all-read");
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const openDetail = (t) => {
    if (t.family_space_child_id) return nav(`/app/circulo/${t.family_space_child_id}`);
    if (t.course_id) return nav(`/app/aula/${t.course_id}`);
  };

  const doDeletePurchase = async () => {
    await api.delete(`/admin/purchases/${deleteTarget.session_id}`);
    setDeleteTarget(null);
    await load();
  };

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="actividad-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver al inicio" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Actividad · Compras y notificaciones" : "Activity · Purchases & notifications"}</span>
      </div>

      <div className="wrap-app max-w-[1100px] py-8" data-testid="actividad-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h2 className="font-display text-[24px] font-medium">{lang === "es" ? "Actividad reciente" : "Recent activity"}</h2>
            <p className="text-tert text-[12.5px] mt-1">
              {lang === "es"
                ? "Identidad completa del comprador, ítem, monto, método y enlace directo a su espacio."
                : "Full buyer identity, item, amount, method and direct link to their space."}
            </p>
          </div>
          <button className="btn-ghost btn text-[12px]" onClick={load} data-testid="actividad-refresh">
            <IconRefresh size={13} /> {lang === "es" ? "Actualizar" : "Refresh"}
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2 mb-6" role="tablist">
          <button
            className={`text-left p-3 rounded-lg border transition-all ${tab === "purchases" ? "border-teal bg-teal-soft" : "border-line bg-white hover:border-teal-deep"}`}
            onClick={() => setTab("purchases")}
            data-testid="tab-purchases"
          >
            <p className="text-[18px] leading-none"><IconReceipt size={20} className="inline" /></p>
            <p className="text-[13px] font-medium mt-1">
              {lang === "es" ? "Compras" : "Purchases"} · <span className="text-teal-deep">{totalPurchases}</span>
            </p>
          </button>
          <button
            className={`text-left p-3 rounded-lg border transition-all ${tab === "notifications" ? "border-teal bg-teal-soft" : "border-line bg-white hover:border-teal-deep"}`}
            onClick={() => setTab("notifications")}
            data-testid="tab-notifications"
          >
            <p className="text-[18px] leading-none"><IconBell size={20} className="inline" /></p>
            <p className="text-[13px] font-medium mt-1">
              {lang === "es" ? "Notificaciones" : "Notifications"}
              {unreadCount > 0 && <span className="ml-2 tag tag-honey">{unreadCount} {lang === "es" ? "nuevas" : "new"}</span>}
            </p>
          </button>
        </div>

        {error && <p className="text-errtext text-[13px] mb-4">{error}</p>}
        {loading ? (
          <div className="card"><IconLoader2 className="animate-spin" /></div>
        ) : tab === "purchases" ? (
          purchases.length === 0 ? (
            <p className="text-tert text-[13px]">{lang === "es" ? "Sin compras aún." : "No purchases yet."}</p>
          ) : (
            <div className="space-y-2">
              {purchases.map((t) => (
                <div
                  key={t.session_id}
                  className={`card flex items-start justify-between gap-3 flex-wrap ${t.payment_status === "paid" ? "" : "opacity-70"}`}
                  data-testid={`purchase-row-${t.session_id}`}
                >
                  <div className="flex-1 min-w-[240px]">
                    <p className="font-medium text-[14px]">
                      <IconUsersGroup size={13} className="inline mr-1 text-teal-deep" />
                      <span data-testid={`purchase-buyer-name-${t.session_id}`}>{t.buyer_name || (t.user_email || "").split("@")[0]}</span>
                      <span className="text-tert text-[11.5px] font-mono ml-2" data-testid={`purchase-buyer-email-${t.session_id}`}>
                        <IconMail size={11} className="inline" /> {t.user_email || "—"}
                      </span>
                    </p>
                    <p className="text-[13px] mt-1" data-testid={`purchase-item-${t.session_id}`}>
                      {t.item_name || t.course_title || t.package_id}
                    </p>
                    <p className="text-tert text-[11.5px] mt-1 font-mono">
                      {new Date(t.created_at).toLocaleString(lang === "es" ? "es-HN" : "en-US")}
                      {" · "}{t.provider}
                      {t.order_id && <> · order <span className="text-teal-deep">{t.order_id}</span></>}
                      {t.capture_id && <> · capture <span className="text-teal-deep">{t.capture_id.slice(0, 12)}…</span></>}
                    </p>
                    {t.family_space_pending && (
                      <p className="text-warntext text-[11.5px] mt-1">
                        {lang === "es" ? "⏳ Espacio familiar aún no configurado por el comprador" : "⏳ Family space not yet configured by buyer"}
                      </p>
                    )}
                    {t.family_space_child_id && (
                      <p className="text-oktext text-[11.5px] mt-1">
                        {lang === "es" ? "✓ Espacio familiar activo" : "✓ Family space active"}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="font-display text-[18px] font-medium">${(t.amount || 0).toFixed(2)} <span className="text-tert text-[10px]">{(t.currency || "usd").toUpperCase()}</span></span>
                    <span className={`tag ${t.payment_status === "paid" ? "bg-okbg text-oktext" : t.payment_status === "pending" ? "bg-warnbg text-warntext" : "bg-errbg text-errtext"}`}>
                      {t.payment_status}
                    </span>
                    {(t.family_space_child_id || t.course_id) && (
                      <button className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => openDetail(t)} data-testid={`purchase-open-${t.session_id}`}>
                        <IconExternalLink size={11} /> {lang === "es" ? "Abrir" : "Open"}
                      </button>
                    )}
                    <button
                      className="btn-ghost btn text-[11px] px-2 py-1 text-errtext"
                      onClick={() => setDeleteTarget(t)}
                      data-testid={`purchase-delete-${t.session_id}`}
                      title={lang === "es" ? "Eliminar compra" : "Delete purchase"}
                    >
                      <IconTrash size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <>
            {unreadCount > 0 && (
              <div className="flex justify-end mb-2">
                <button className="btn-ghost btn text-[12px]" onClick={markAllRead} data-testid="mark-all-read">
                  <IconCheck size={12} /> {lang === "es" ? "Marcar todas como leídas" : "Mark all as read"}
                </button>
              </div>
            )}
            {notifs.length === 0 ? (
              <p className="text-tert text-[13px]">{lang === "es" ? "Sin notificaciones." : "No notifications."}</p>
            ) : (
              <div className="space-y-2">
                {notifs.map((n) => (
                  <div
                    key={n.id}
                    className={`card border-l-[4px] ${!n.read ? "border-l-honey bg-honey-soft" : "border-l-line"}`}
                    data-testid={`notif-${n.id}`}
                  >
                    <p className="text-[13.5px]">
                      <span className={`tag mr-2 ${n.kind === "family_space_created" ? "bg-teal-soft text-teal-deep" : "bg-honey-soft text-honey-deep"}`}>
                        {n.kind === "family_space_created"
                          ? (lang === "es" ? "Espacio familiar" : "Family space")
                          : (lang === "es" ? "Compra" : "Purchase")}
                      </span>
                      <strong data-testid={`notif-name-${n.id}`}>{n.buyer_name}</strong>
                      <span className="text-tert text-[11.5px] font-mono ml-2">{n.buyer_email}</span>
                    </p>
                    <p className="text-[13px] mt-1 text-muted">
                      {n.kind === "family_space_created" ? (
                        <>{lang === "es" ? "creó el espacio de" : "created space for"} <strong>{n.child_name}</strong> · {n.invitees_count} {lang === "es" ? "invitados" : "invitees"}</>
                      ) : (
                        <>{lang === "es" ? "compró" : "bought"} <strong>{n.item_name}</strong> · ${(n.amount || 0).toFixed(2)} {(n.currency || "usd").toUpperCase()}</>
                      )}
                    </p>
                    <p className="text-tert text-[11.5px] mt-1 font-mono">
                      {new Date(n.created_at).toLocaleString(lang === "es" ? "es-HN" : "en-US")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={doDeletePurchase}
        title={lang === "es" ? "Eliminar registro de compra" : "Delete purchase record"}
        description={
          deleteTarget
            ? (lang === "es"
                ? `Se eliminará la compra <b>${deleteTarget.item_name || deleteTarget.course_title || deleteTarget.package_id || deleteTarget.session_id}</b> de <b>${deleteTarget.buyer_name || deleteTarget.user_email || "—"}</b> por <b>$${(deleteTarget.amount || 0).toFixed(2)}</b>. Esta acción es irreversible y solo afecta el registro contable, no reembolsa al comprador.`
                : `Purchase <b>${deleteTarget.item_name || deleteTarget.course_title || deleteTarget.package_id || deleteTarget.session_id}</b> from <b>${deleteTarget.buyer_name || deleteTarget.user_email || "—"}</b> for <b>$${(deleteTarget.amount || 0).toFixed(2)}</b> will be removed. This is irreversible and does not refund the buyer.`)
            : ""
        }
        testId="purchase-delete-modal"
      />
    </>
  );
}
